// Copyright (c) StrangeBee 2023

import React, { useEffect, useState, ReactElement, useCallback } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import lodash from 'lodash';
import WsConfigForm from '@Organisms/FormConfig/WsConfigForm/WsConfigForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { OrgSelector } from '@Organisms/FormConfig/OrgSelector/OrgSelector';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { handleTestServerError, openMessage } from '@Helpers/';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import useValidateForm from '@Hooks/useValidateForm';
import { urlValidator } from '@Helpers/string';
import client from '@Client';
import { CortexServerInput } from 'thehive-sdk';
import { computeWsConfig } from '@Helpers/wsConfig';

import './CortexDrawer.scss';

const nameKey = {
    name: ['name'],
    url: ['url'],
    auth: {
        key: ['auth', 'key'],
    },
    wsConfig: ['wsConfig'],
    includedTheHiveOrganisations: ['includedTheHiveOrganisations'],
    excludedTheHiveOrganisations: ['excludedTheHiveOrganisations'],
};

export const resetFields = {
    name: '',
    url: '',
    auth: {
        type: 'bearer',
        key: '',
    },
    wsConfig: {
        proxy: {
            protocol: 'http',
        },
        ssl: {
            loose: {
                checkCertificateAuthority: true,
                acceptAnyCertificate: false,
            },
        },
    },
    filterOrganization: 'all',
    includedTheHiveOrganisations: [] as string[],
    excludedTheHiveOrganisations: [] as string[],
};

interface CortexDrawerProps {
    data: Record<string, any>;
    mode?: 'edit' | 'add';
    triggerElem?: ReactElement;
    visible?: boolean;
    afterVisible?: (visible: boolean) => void;
    onSubmit: (state: any) => void;
    className?: string;
}

const CortexDrawer = ({
    className,
    onSubmit,
    data,
    triggerElem,
    mode = 'edit',
    visible,
    afterVisible,
}: CortexDrawerProps): ReactElement => {
    const i = useLocalMessage();

    const dataCurrent = data;

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const [initialValues, setInitialValues] = useState<Record<string, any>>({});
    const [filterUsed, setFilterUsed] = useState<string>();
    const [testLoading, setTestLoading] = useState<boolean>(false);

    const computeConfig = useCallback(
        (formValues: CortexServerInput) => {
            const result = lodash.merge(dataCurrent, formValues);
            delete result.another;

            const tmpFilter = result.filterOrganization;

            const filterOrganization = {
                includedTheHiveOrganisations: ['*'],
                excludedTheHiveOrganisations: [] as string[],
            };

            if (tmpFilter === 'include') {
                filterOrganization.includedTheHiveOrganisations = formValues.includedTheHiveOrganisations.filter(
                    (name: string) => name !== '*',
                );
                filterOrganization.excludedTheHiveOrganisations = [];
            }

            if (tmpFilter === 'exclude') {
                filterOrganization.includedTheHiveOrganisations = ['*'];
                filterOrganization.excludedTheHiveOrganisations = formValues.excludedTheHiveOrganisations;
            }

            const wsConfig = computeWsConfig(result.wsConfig, formValues.wsConfig);

            const newResult = {
                ...result,
                auth: { ...formValues.auth, type: 'bearer' },
                wsConfig,
                ...filterOrganization,
            };

            return newResult;
        },
        [dataCurrent],
    );

    const handleTest = () => {
        const formValues: CortexServerInput = form.getFieldsValue();
        const config = computeConfig(formValues);

        setTestLoading(true);

        client.CortexController.test(config)
            .then(() => {
                openMessage('success', i('configPlateform.message.cortex.testSuccess'));
            })
            .catch(handleTestServerError)
            .finally(() => setTestLoading(false));
    };

    const computeOnFinishHandler = (toggleDrawer: () => void) => (formValues: CortexServerInput) => {
        const config = computeConfig(formValues);

        onResetForm();
        toggleDrawer();
        setInitialValues(config);
        onSubmit(config);
    };

    const onChangeFilter = (value: string) => {
        setFilterUsed(value);
    };

    const onResetForm = () => {
        setFieldsTouched(false);
        onChangeFilter(initialValues.filterOrganization);
        form.resetFields();
    };

    useEffect(() => {
        let filterOrganization = initialValues.filterOrganization || 'all';

        if (initialValues.excludedTheHiveOrganisations && initialValues.excludedTheHiveOrganisations.length) {
            filterOrganization = 'exclude';
        }

        if (
            initialValues.includedTheHiveOrganisations &&
            initialValues.includedTheHiveOrganisations.length &&
            !initialValues.includedTheHiveOrganisations.includes('*')
        ) {
            filterOrganization = 'include';
        }

        onChangeFilter(filterOrganization);

        form.setFieldsValue({ ...initialValues, filterOrganization });
    }, [initialValues]); //eslint-disable-line

    useEffect(() => {
        const dataCurrentClone = lodash.cloneDeep(dataCurrent);
        const resetFieldsClone = lodash.cloneDeep(resetFields);
        const result = lodash.merge(resetFieldsClone, dataCurrentClone);
        result.wsConfig.ssl.loose.checkCertificateAuthority = !result.wsConfig.ssl.loose.acceptAnyCertificate;

        setInitialValues(result);
    }, [dataCurrent]);

    return (
        <THDrawer
            title={
                mode === 'edit'
                    ? i('configPlateform.drawer.titleEdit', { name: dataCurrent?.name || '' })
                    : i('configPlateform.drawer.titleAdd')
            }
            className={classList('config-cortex__drawer', className)}
            triggerElem={triggerElem}
            visible={visible}
            disableClose={fieldsTouched}
            afterVisibleChange={(visible) => {
                afterVisible?.(visible);
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    className="cortex-form"
                    onFinish={computeOnFinishHandler(toggleDrawer)}
                    onValuesChange={() => {
                        setFieldsTouched(true);
                        onFormValuesChange();
                    }}
                    form={form}
                    initialValues={initialValues}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid}
                                onCancel={() => {
                                    onResetForm();
                                    toggleDrawer();
                                }}
                                footerText={i(
                                    mode === 'edit'
                                        ? 'configPlateform.action.confirmeEdit'
                                        : 'configPlateform.action.confirmeAdd',
                                )}
                                anotherText={i('configPlateform.action.testConnection')}
                                onAnother={handleTest}
                                anotherDisabled={!isValid && mode !== 'edit'}
                                anotherLoading={testLoading}
                            />
                        }
                    >
                        <div className="cortex-form__content">
                            <div className="cortex-form__section">
                                <TitleSection>{i('configPlateform.section.principal')}</TitleSection>
                                <THForm.Item
                                    name={nameKey.name}
                                    label={i('configPlateform.field.nameServer')}
                                    rules={[{ required: !data.name, message: i('configPlateform.required.general') }]}
                                >
                                    <THInput disabled={data.name} />
                                </THForm.Item>

                                <THForm.Item
                                    name={nameKey.url}
                                    label={i('configPlateform.field.urlServer')}
                                    rules={[
                                        {
                                            required: true,
                                            message: i('configPlateform.required.general'),
                                        },
                                        {
                                            validator: urlValidator,
                                            message: i('configPlateform.required.url'),
                                        },
                                    ]}
                                >
                                    <THInput autoComplete="new-password" />
                                </THForm.Item>

                                <THForm.Item
                                    name={nameKey.auth.key}
                                    label={i('configPlateform.field.api-key')}
                                    rules={[{ required: true, message: i('configPlateform.required.general') }]}
                                >
                                    <THPassword autoComplete="new-password" />
                                </THForm.Item>
                            </div>

                            <div className="cortex-form__section">
                                <WsConfigForm name={nameKey.wsConfig} form={form} />
                            </div>

                            <div className="cortex-form__section">
                                <TitleSection>{i('configPlateform.section.advancedSetting')}</TitleSection>
                                <THForm.Item
                                    name={'filterOrganization'}
                                    label={i('configPlateform.field.filterOrganization')}
                                >
                                    <THSelect
                                        placeholder="Choose filter organisation"
                                        options={[
                                            { value: 'all', label: i('configPlateform.options.all') },
                                            { value: 'include', label: i('configPlateform.options.include') },
                                            { value: 'exclude', label: i('configPlateform.options.exlude') },
                                        ]}
                                        onChange={onChangeFilter}
                                    />
                                </THForm.Item>

                                <THForm.Item
                                    name={nameKey.includedTheHiveOrganisations}
                                    label={i('configPlateform.field.includedTheHiveOrganisations')}
                                    hidden={filterUsed !== 'include'}
                                >
                                    <OrgSelector />
                                </THForm.Item>

                                <THForm.Item
                                    name={nameKey.excludedTheHiveOrganisations}
                                    label={i('configPlateform.field.excludedTheHiveOrganisations')}
                                    hidden={filterUsed !== 'exclude'}
                                >
                                    <OrgSelector />
                                </THForm.Item>
                            </div>
                        </div>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};

export default CortexDrawer;
